import { IHttpApi } from '@cian/http-api/shared';

import { TGeoType } from '../filters/state/geo';
import { repeatTask } from '../utils/repeat_task';
import { IMakeRequest } from '../utils/request';
import { loadYmapsApi } from '../utils/ymaps';

import {
  IUnderConstructiontionInfo,
  createUnderConstructionModel,
} from '@cian/api-models/suggest/v1/undergrounds-under-construction';
import {
  createSuggestRailwaysModel,
  ISuggestRailwaysModelRequest,
  ISuggestRailwayData,
} from '@cian/api-models/suggest/suggest-railways';
import { ILogger } from '@cian/logger';
import { ILocation } from './locations';

export interface IGeocodeCachedResult {
  text: string;
  name: string;
  kind: YMaps.TGeoKind;
  coordinates: YMaps.TCoord;
  boundedBy: [YMaps.TCoord, YMaps.TCoord];
}

export interface IGeocodeForSearchRequest {
  text: string;
  kind: YMaps.TGeoKind;
  coordinates: YMaps.TCoord;
}

export interface IGeoLocationCatalogLink {
  id: number;
  objectType: TGeoType;
}

// tslint:disable-next-line:max-line-length
export type TGeocodeForSearchGeoType =
  | 'Country'
  | 'Location'
  | 'Street'
  | 'Road'
  | 'District'
  | 'Underground'
  | 'House';

export interface IGeocodeForSearchResponse {
  geo: {
    lat: number;
    lng: number;
  };
  geoLocationCatalogLink: IGeoLocationCatalogLink | null;
  text: string;
  isParsed: boolean;
  details: IGeoDetail[];
  countryId: number;
  regionId: number;
  microDistricts: [
    {
      fullName: string;
      hasBoundaries: boolean;
      id: number;
      locationFullName: string;
      locationId: number;
      name: string;
      nativeName: string;
    },
  ];
}

export interface IGeoDetail {
  id: number;
  name: string;
  fullName: string;
  geoType: TGeocodeForSearchGeoType;
  locationInfo: {
    text: string;
    hasMetro: boolean;
    hasHighway: boolean;
    hasDistricts: boolean;
    hasNeighbors: boolean;
    parentId: number | null;
    mainTownId: number | null;
    lat: number;
    lng: number;
    boundedBy: {
      lowerCorner: {
        lat: number;
        lng: number;
      };
      upperCorner: {
        lat: number;
        lng: number;
      };
    };
  };
}

export interface IGeoDistrict {
  id: number;
  name: string;
  type: 'Raion' | 'Okrug' | 'MikroRaion';
  childs: IGeoDistrict[];
  direction: {
    code: number | null;
    name: string | null;
  };
}

export interface IGeoDistrictsTreeResponse {
  data: IGeoDistrict[];
}

function isScriptError(item: {}): item is YMaps.IScriptError {
  return item && 'message' in item && (item as { message?: string }).message === 'scriptError';
}

export function suggest(request: string, options?: YMaps.ISuggestOptions): Promise<YMaps.ISuggestResult[]> {
  return repeatTask(3, () =>
    loadYmapsApi({ require: ['suggest'] })
      .then(ymaps => ymaps.suggest(request, options))
      .then(items => {
        if (isScriptError(items)) {
          throw new Error(items.message);
        }

        return items || [];
      }),
  );
}

export function geocodeCached(makeRequest: IMakeRequest, request: string): Promise<IGeocodeCachedResult[]> {
  return makeRequest({
    uri: {
      path: '/api/geo/geocode-cached',
      query: `request=${encodeURIComponent(request)}`,
    },
    method: 'GET',
  }).then((res: { items: IGeocodeCachedResult[] }) => res.items);
}

export function geocode(request: string, options?: YMaps.IGeocodeOptions) {
  return loadYmapsApi({ require: ['geocode'] })
    .then(ymaps => ymaps.geocode(request, options))
    .then(items => items || []);
}

export function geocodeForSearch(
  makeRequest: IMakeRequest,
  request: IGeocodeForSearchRequest,
): Promise<IGeocodeForSearchResponse> {
  return makeRequest({
    uri: {
      path: '/api/geo/geocoded-for-search',
    },
    headers: [['Content-Type', 'application/x-www-form-urlencoded']],
    method: 'POST',
    body: `Lng=${encodeURIComponent(String(request.coordinates[0]))}&Lat=${encodeURIComponent(
      String(request.coordinates[1]),
    )}&Kind=${encodeURIComponent(request.kind)}&Address=${encodeURIComponent(request.text)}`,
  });
}

export function getUnderConstruction(httpApi: IHttpApi): Promise<IUnderConstructiontionInfo[]> {
  return httpApi.fetch(createUnderConstructionModel({})).then(resp => {
    return resp.response.undergrounds;
  });
}

export function requestDistrictsTree(makeRequest: IMakeRequest, locationId: number): Promise<IGeoDistrict[]> {
  return makeRequest({
    uri: {
      path: '/api/geo/get-districts-tree',
      query: `locationId=${locationId}`,
    },
    method: 'GET',
  }).then((res: IGeoDistrict[]) => {
    return res;
  });
}

export function getSuggestRailways(
  httpApi: IHttpApi,
  parameters: ISuggestRailwaysModelRequest,
  logger: ILogger,
): Promise<ISuggestRailwayData[]> {
  return httpApi.fetch(createSuggestRailwaysModel({ parameters })).then(resp => {
    if (resp.statusCode === 200) {
      return resp.response.data;
    }

    const { regionId } = parameters;

    const dataError = new Error(`Failed to get railways for regionId = ${regionId}`);
    logger.error(dataError);

    return [];
  });
}

export interface IGetLocationResponse {
  data: ILocation;
  status: 'ok';
}

export interface IGeocodeError {
  message: string;
  errors: {
    key: string;
    message: string;
  }[];
}

export function isSuccessResponse<S, E>(response: S | E, key: string): response is S {
  // tslint:disable-next-line:no-any
  return key in (response as any);
}

export function getLocationById(makeRequest: IMakeRequest, regionOrMaintownId: number): Promise<ILocation> {
  return makeRequest({
    method: 'GET',
    uri: {
      path: `/cian-api/site/v1/get-location/?regionId=${regionOrMaintownId}`,
    },
  }).then((response: IGetLocationResponse | IGeocodeError) => {
    if (!isSuccessResponse<IGetLocationResponse, IGeocodeError>(response, 'data')) {
      throw new Error();
    }

    return response.data;
  });
}
